import { useState } from 'react';
import axios from 'axios';
import { Percent, Plus, Tag, Trash2, Edit2, X, Check, Calendar, ChevronDown, Building2 } from 'lucide-react';

import { API_URL as API } from '../../config/api';

export default function PartnerOffers({ data, token, onChange }) {
  const offers = data?.offers || [];
  const turfs = data?.turfs || [];
  const now = new Date();
  
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    discount: '',
    description: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    turf_ids: [],
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openCreate = () => {
    setEditingOffer(null);
    setForm({
      title: '',
      discount: '',
      description: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: '',
      turf_ids: [],
    });
    setDropdownOpen(false);
    setShowForm(true);
    setError('');
  };

  const openEdit = (offer) => {
    setEditingOffer(offer);
    let tids = offer.turf_ids || [];
    if (tids.length === 0 && offer.turf_id) {
      tids = [offer.turf_id._id || offer.turf_id];
    }
    setForm({
      title: offer.title || '',
      discount: offer.discount || '',
      description: offer.description || '',
      valid_from: offer.valid_from ? offer.valid_from.split('T')[0] : new Date().toISOString().split('T')[0],
      valid_until: offer.valid_until ? offer.valid_until.split('T')[0] : '',
      turf_ids: tids,
    });
    setDropdownOpen(false);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.turf_ids || form.turf_ids.length === 0) {
      setError('Please select at least one venue');
      return;
    }
    setSaving(true);
    setError('');
    
    try {
      if (editingOffer) {
        await axios.put(`${API}/offers/${editingOffer._id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/offers`, form, { headers: { Authorization: `Bearer ${token}` } });
      }
      setForm({
        title: '',
        discount: '',
        description: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: '',
        turf_ids: [],
      });
      setShowForm(false);
      setEditingOffer(null);
      onChange?.();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save offer');
    } finally {
      setSaving(false);
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    try {
      await axios.delete(`${API}/offers/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to delete offer');
    }
  };

  const toggleTurfSelection = (turfId) => {
    if (form.turf_ids.includes(turfId)) {
      setForm({ ...form, turf_ids: form.turf_ids.filter(id => id !== turfId) });
    } else {
      setForm({ ...form, turf_ids: [...form.turf_ids, turfId] });
    }
  };

  const toggleSelectAll = () => {
    if (form.turf_ids.length === turfs.length) {
      setForm({ ...form, turf_ids: [] });
    } else {
      setForm({ ...form, turf_ids: turfs.map(t => t._id || t.id) });
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.5s ease-out' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#161616', marginBottom: '8px' }}>Offers & Promotions</h2>
          <p style={{ color: '#374151', fontWeight: '500' }}>Offers attached to your venues</p>
        </div>
        <button onClick={openCreate} style={btnPrimary}><Plus size={18} /> Create New Offer</button>
      </div>

      {/* CREATE / EDIT FORM */}
      {showForm && (
        <form onSubmit={handleSubmit} style={{ background: 'white', padding: '1.8rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', marginBottom: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', position: 'relative' }}>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ fontWeight: '800', fontSize: '1.1rem', color: '#161616', margin: 0 }}>
              {editingOffer ? 'Edit Offer' : 'New Offer'}
            </h3>
            <button type="button" onClick={() => { setShowForm(false); setEditingOffer(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#111827', display: 'flex', alignItems: 'center' }}>
              <X size={20} />
            </button>
          </div>
          
          <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Offer title *" style={inputStyle} />
          <input required value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} placeholder="Discount, e.g. 20% OFF *" style={inputStyle} />
          
          {/* Custom Venue Multi-Select Dropdown */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>Select Venues *</label>
            <div 
              onClick={() => setDropdownOpen(!dropdownOpen)} 
              style={{ 
                ...inputStyle, 
                cursor: 'pointer', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: 'white'
              }}
            >
              <span style={{ color: form.turf_ids.length === 0 ? '#9ca3af' : '#161616' }}>
                {form.turf_ids.length === 0 
                  ? 'Select one or more venues' 
                  : form.turf_ids.length === turfs.length 
                    ? 'All Venues Selected' 
                    : `${form.turf_ids.length} Venue(s) Selected`}
              </span>
              <ChevronDown size={18} style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: '#6b7280' }} />
            </div>
            
            {dropdownOpen && (
              <div style={{ 
                background: 'white', 
                border: '1.5px solid #EEF2E6', 
                borderRadius: '12px', 
                marginTop: '6px', 
                maxHeight: '180px', 
                overflowY: 'auto', 
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                padding: '6px'
              }}>
                <div 
                  onClick={toggleSelectAll}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px', 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    background: '#F8FAF7',
                    marginBottom: '4px',
                    color: '#084734'
                  }}
                >
                  <input 
                    type="checkbox" 
                    checked={form.turf_ids.length === turfs.length && turfs.length > 0} 
                    onChange={() => {}} 
                    style={{ cursor: 'pointer' }}
                  />
                  <span>Select All Venues</span>
                </div>
                {turfs.map(turf => {
                  const turfId = turf._id || turf.id;
                  const isChecked = form.turf_ids.includes(turfId);
                  return (
                    <div 
                      key={turfId}
                      onClick={() => toggleTurfSelection(turfId)}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        padding: '8px 12px', 
                        borderRadius: '8px', 
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: '#374151',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F8FAF7'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <input 
                        type="checkbox" 
                        checked={isChecked} 
                        onChange={() => {}} 
                        style={{ cursor: 'pointer' }}
                      />
                      <span>{turf.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Start Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151' }}>Start Date *</label>
            <input 
              type="date" 
              required 
              value={form.valid_from} 
              onChange={e => setForm({ ...form, valid_from: e.target.value })} 
              style={inputStyle} 
              min={new Date().toISOString().split('T')[0]} 
            />
          </div>
          
          {/* Expiry Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151' }}>Expiry Date *</label>
            <input 
              type="date" 
              required 
              value={form.valid_until} 
              onChange={e => setForm({ ...form, valid_until: e.target.value })} 
              style={inputStyle} 
              min={form.valid_from || new Date().toISOString().split('T')[0]} 
            />
          </div>
          
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" style={{ ...inputStyle, gridColumn: '1 / -1', minHeight: '80px', resize: 'vertical' }} />
          {error && <div style={{ gridColumn: '1 / -1', color: '#DC2626', fontWeight: '700', fontSize: '0.85rem', background: '#FEE2E2', padding: '10px 14px', borderRadius: '10px' }}>{error}</div>}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={saving || turfs.length === 0} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              <Check size={16} /> {saving ? 'Saving...' : editingOffer ? 'Update Offer' : 'Save Offer'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingOffer(null); }} style={btnSecondary}>Cancel</button>
          </div>
        </form>
      )}

      {offers.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2.5rem' }}>
           {offers.map(o => {
             const expired = o.valid_until && new Date(o.valid_until) < now;
             const status = expired ? 'Expired' : 'Active';
             
             // Resolve names of selected venues
             const selectedTids = o.turf_ids || (o.turf_id?._id ? [o.turf_id._id] : []);
             const applicableVenues = turfs.filter(t => selectedTids.includes(t._id || t.id));
             const venueNames = applicableVenues.length > 0 
               ? applicableVenues.map(t => t.name).join(', ') 
               : (o.turf_id?.name || 'All venues');

             const validFromStr = o.valid_from ? new Date(o.valid_from).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '';
             const validUntilStr = o.valid_until ? new Date(o.valid_until).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
             const validityRange = validFromStr ? `${validFromStr} - ${validUntilStr}` : (validUntilStr ? `Until ${validUntilStr}` : 'Ongoing');

             return (
                <div key={o._id} style={{ background: 'white', padding: '1.5rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                   <div>
                     <div style={{ position: 'absolute', top: 0, right: 0, padding: '8px 15px', background: status === 'Active' ? '#DCEFB8' : '#EEF2E6', color: status === 'Active' ? '#084734' : '#111827', fontSize: '0.7rem', fontWeight: '800', borderRadius: '0 0 0 16px' }}>
                        {status.toUpperCase()}
                     </div>
                     <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#DCEFB8', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
                        <Percent size={24} />
                     </div>
                     <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#161616', marginBottom: '4px' }}>{o.title}</h3>
                     <p style={{ fontSize: '1.05rem', fontWeight: '800', color: '#084734', marginBottom: '10px' }}>{o.discount || 'Offer'}</p>
                     
                     <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.78rem', color: '#4b5563', fontWeight: '600', marginBottom: '8px' }}>
                       <Building2 size={14} style={{ marginTop: '2px', flexShrink: 0 }} />
                       <span style={{ lineHeight: 1.3 }}>{venueNames}</span>
                     </div>

                     <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#4b5563', fontWeight: '600', marginBottom: '1.5rem' }}>
                       <Calendar size={14} />
                       <span>{validityRange}</span>
                     </div>
                     
                     <p style={{ fontSize: '0.8rem', color: '#4b5563', fontWeight: '500', marginBottom: '1.5rem', lineHeight: 1.4 }}>{o.description || 'No description added'}</p>
                   </div>

                   <div style={{ borderTop: '1.5px solid #EEF2E6', paddingTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button onClick={() => openEdit(o)} style={iconBtn} title="Edit offer"><Edit2 size={16} /></button>
                      <button onClick={() => deleteOffer(o._id)} style={{ ...iconBtn, color: '#ef4444' }} title="Delete offer"><Trash2 size={16} /></button>
                   </div>
                </div>
             );
           })}
        </div>
      ) : (
        <div style={{ background: 'white', padding: '4rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', textAlign: 'center', color: '#111827', fontWeight: '700', marginBottom: '2.5rem' }}>
          No offers yet. Click "Create New Offer" to add your first promotion.
        </div>
      )}

      <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', border: '1.5px solid #EEF2E6', display: 'flex', gap: '2rem', alignItems: 'center' }}>
         <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#DCEFB8', color: '#084734', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Tag size={28} />
         </div>
         <div style={{ flex: 1 }}>
            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', marginBottom: '4px' }}>Grow your business with smart offers</h4>
            <p style={{ fontSize: '0.85rem', color: '#374151', fontWeight: '500', lineHeight: 1.5 }}>
               Venues with active offers get up to 30% more bookings. Create time-limited discounts to fill off-peak slots.
            </p>
         </div>
         <button onClick={openCreate} style={btnPrimary}><Plus size={16} /> Add Offer</button>
      </div>
    </div>
  );
}

const btnPrimary = { background: '#084734', color: '#CEF17B', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: '800', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 8px 20px rgba(8,71,52,0.2)' };
const btnSecondary = { background: 'white', color: '#374151', border: '1.5px solid #E9EDE8', padding: '12px 24px', borderRadius: '12px', fontWeight: '700', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const iconBtn = { background: 'none', border: '1.5px solid #E9EDE8', padding: '6px 8px', borderRadius: '8px', color: '#111827', cursor: 'pointer', display: 'flex', alignItems: 'center' };
const inputStyle = { padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #EEF2E6', fontSize: '0.9rem', fontWeight: '600', outline: 'none', width: '100%', boxSizing: 'border-box' };
